import {
  Button,
  Container,
  FormControl,
  FormHelperText,
  Grid,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { Theme, useTheme } from "@mui/material/styles";
import ChangeReasonDialog from "components/ChangeReasonDialog";
import EntityDetails from "components/EntityDetails";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import SelectParentUserSku from "components/SelectParentUserSku";
import SnackbarMessage from "components/SnackbarMessage";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import { useMemo, useState } from "react";
import { Field, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import {
  ChangeParentUserSkuRequest,
  OrganizationUserDetails,
  ResponseCode,
  ResponseOfUserSkuExtended,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";

const useStyles = makeStyles((theme: Theme) => ({
  changeParentUserSku: {
    mb: theme.spacing(3),
  },
  updateParentUserSkuButton: {
    mt: theme.spacing(2),
  },
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  selectParentUserSku: {
    minWidth: 300,
  },
}));

type ChangeParentUserSkuFormValues = Omit<
  ChangeParentUserSkuRequest,
  "userId" | "userSkuId" | "init" | "toJSON"
> & {
  changeReason: string;
};

const { Form } = withTypes<ChangeParentUserSkuFormValues>();

const ChangeParentUserSkuPage = () => {
  const { userId, userSkuId } = useParams<{
    userId: string;
    userSkuId: string;
  }>();
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();

  const {
    organizationUserService,
    userSkuService,
    userCourseUrl,
  } = useGlobalContext();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const [formValues, setFormValues] = useState<ChangeParentUserSkuFormValues>();
  const [changeReason, setChangeReason] = useState("");
  const [changeReasonDialogOpen, setChangeReasonDialogOpen] = useBoolean(false);

  const userResult = useQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", userId],
    () => organizationUserService.getOrganizationUserDetails(userId!),
    {
      enabled: !!userId,
    }
  );

  const user = userResult?.data;

  const userSkuResult = useQuery<ResponseOfUserSkuExtended>(
    ["getUserSku", userSkuId],
    () =>
      userSkuService.getUserSku(
        user?.id,
        user?.organizationId,
        userSkuId!
      ),
    { enabled: !!userResult && !userResult.isLoading }
  );

  const userSku = userSkuResult?.data?.data;

  useTitle(t({ id: "changeParentUserSkuTitle" }, { name: userSku?.title }));

  const parentUserSkuResult = useQuery<ResponseOfUserSkuExtended>(
    ["getParentUserSku", userSku?.parentUserSkuId],
    () =>
      userSkuService.getUserSku(
        user?.id,
        user?.organizationId,
        userSku?.parentUserSkuId!
      ),
    {
      enabled:
        !!userSku && !userSkuResult.isLoading && !!userSku?.parentUserSkuId,
    }
  );

  const parentUserSku = parentUserSkuResult?.data?.data;

  const loading =
    userResult.isLoading ||
    userSkuResult.isLoading ||
    parentUserSkuResult.isFetching;

  const rows = [
    RowElement(
      "userNameLabel",
      `${user?.firstName ?? ""} ${user?.lastName ?? ""}`,
      loading
    ),
    RowElement("emailLabel", user?.email!, loading),
    RowElement("userId", user?.id!, loading),
    RowElement("skuLabel", userSku?.sku ?? "", loading),
    RowElement("skuTitleColumnLabel", userSku?.title ?? "", loading),
    RowElement("currentParentUserSkuLabel", parentUserSku?.sku ?? "", loading),
    RowElement(
      "currentParentUserSkuTitleLabel",
      parentUserSku?.title ?? "",
      loading
    ),
  ].filter((r) => !!r.component);

  const init = useMemo<ChangeParentUserSkuRequest>(() => {
    return ChangeParentUserSkuRequest.fromJS({
      userId: userId!,
      userSkuId: userSkuId!,
    });
  }, [userId, userSkuId]);

  const onChangeParentUserSku = async () => {
    if (!formValues) {
      return;
    }

    const response = await userSkuService.changeParentUserSku(
      new ChangeParentUserSkuRequest({
        ...formValues,
        userId: userId!,
        userSkuId: userSkuId!,
        organizationId: user!.organizationId,
        changeReason,
      })
    );

    if (response.responseCode === ResponseCode.OK) {
      queryClient.invalidateQueries(["getUserSku", userSkuId]);
      queryClient.invalidateQueries([
        "getParentUserSku",
        userSku!.parentUserSkuId,
      ]);

      setChangeReasonDialogOpen.setFalse();

      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "changeParentUserSkuSuccessLabel" }),
          Severity.Success
        )
      );
    }
  };

  if (
    userResult.isError ||
    userSkuResult.isError ||
    parentUserSkuResult.isError
  ) {
    return (
      <Alert severity="error">
        <pre>{t({ id: "fetchError" })}</pre>
      </Alert>
    );
  }

  return (
    <Container>
      <RedirectBackButton
        redirectRoute={userCourseUrl(userId, userSkuId)}
        labelKey="backToUserCourse"
      />

      <h1>{t({ id: "changeParentUserSku" })}</h1>

      <EntityDetails
        title={t({ id: "registrationDetails" })}
        details={rows}
        sx={styles.changeParentUserSku}
      />

      <Form
        onSubmit={(values) => {
          setFormValues(values);
          setChangeReasonDialogOpen.setTrue();
        }}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError, values }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} mr={2} alignItems="center">
              <Grid item>
                <Field name="parentUserSkuId" required>
                  {({ input, meta }) => (
                    <FormControl
                      fullWidth
                      sx={styles.selectParentUserSku}
                      error={meta.error && meta.submitFailed}
                    >
                      <SelectParentUserSku
                        {...input}
                        organizationId={user?.organizationId!}
                        userId={userId!}
                        currentParentUserSkuId={parentUserSku?.id}
                        label={t({
                          id: "newParentUserSkuLabel",
                        })}
                        disabled={loading}
                        textFieldProps={{
                          error: meta.error && meta.submitFailed,
                        }}
                      />
                      {meta.error && meta.submitFailed && (
                        <FormHelperText>{meta.error}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </Field>
              </Grid>

              <Grid item>
                <Button
                  sx={styles.updateParentUserSkuButton}
                  variant="contained"
                  color="primary"
                  disabled={
                    loading ||
                    submitting ||
                    values.parentUserSkuId == null ||
                    changeReasonDialogOpen
                  }
                  onClick={() => handleSubmit()}
                >
                  <T id="updateParentUserSkuLabel" />
                </Button>
              </Grid>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </form>
        )}
      />

      <ChangeReasonDialog
        open={changeReasonDialogOpen}
        onCancel={setChangeReasonDialogOpen.setFalse}
        setChangeReason={setChangeReason}
        handleConfirm={onChangeParentUserSku}
        changeReason={changeReason}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default ChangeParentUserSkuPage;
